import React from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink, useTheme } from '@mui/material';
import { lighten } from '@mui/system';
import getIconForType from '../../utils/icons/getIconForType';
import placeholderImage from '../../images/placeholder-grey-image.jpg';
import avatarPlaceholder from '../../images/avatar-cropped.png';
import { QrCodeIcon } from 'icons/materialIcons';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useEventTracking } from '../../services/eventTrackingService';
import { formatPhoneNumber, isPhoneContactType } from '../../utils/phoneUtils';

const CardDesign2 = ({
    cardId,
    cardFullName,
    cardJobTitle,
    cardCompany,
    cardTheme,
    cardProfilePictureUrl,
    cardCoverPhotoUrl,
    cardCompanyLogoUrl,
    contextCardLayout,
    cardLinkColor,
    cardLocation,
    cardBio,
    cardPronoun,
    formattedPronoun,
    visibleContactLinks,
    previewContactLink,
    handleSaveContact
}) => {
    const theme = useTheme();
    const cardThemeValues = theme.card;
    const design2Theme = cardThemeValues.cardDesign2;

    const tracking = useEventTracking();
    const cardAccentColor = cardTheme || cardLinkColor || '#F06411';
    const secondaryAccentColor = lighten(cardAccentColor, 0.9);
    const gradientColor = lighten(cardAccentColor, 0.2);
    const handleLinkClick = (link, linkType) => {
        if (tracking && link?.details) {
            tracking.trackLinkTap({
                linkType: linkType,
                linkUrl: link.details.linkUrl,
                linkTitle: link.details.linkTitle || linkType,
                cardId: cardId
            });
        }

        if (link?.details?.formattedLink) {
            window.open(link.details.formattedLink, '_blank');
        } else if (link?.details?.linkUrl) {
            const url = link.details.linkUrl.startsWith('http') ?
                link.details.linkUrl :
                `https://${link.details.linkUrl}`;
            window.open(url, '_blank');
        }
    };

    const allLinks = React.useMemo(() => {
        if (!previewContactLink) return visibleContactLinks || [];
        const linkExists = (visibleContactLinks || []).some(
            link => link.type === previewContactLink.type &&
                link.details?.linkUrl === previewContactLink.details?.linkUrl
        );
        return linkExists ? visibleContactLinks : [...(visibleContactLinks || []), previewContactLink];
    }, [visibleContactLinks, previewContactLink]);

    const contactLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            ['email', 'phone', 'whatsapp', 'sms', 'call'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);

    const socialLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            !['email', 'phone', 'whatsapp', 'sms', 'call', 'website', 'address', 'map'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);

    const websiteLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            ['website', 'address', 'map'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);
    const renderLinkCard = (link, index) => {
        if (!link || !link.details?.linkUrl) return null;

        return (
            <Box
                key={index}
                onClick={() => handleLinkClick(link, link.type)}
                sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    mb: 2,
                    cursor: 'pointer'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: secondaryAccentColor,
                        borderRadius: '0.5rem',
                        transition: 'transform 0.2s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: design2Theme.contactIcon.width,
                            height: design2Theme.contactIcon.height,
                            borderRadius: design2Theme.contactIcon.borderRadius,
                            backgroundColor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            p: 1
                        }}
                    >
                        {link.details?.icon ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${link.details.icon})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: '0.25rem',
                                }}
                            />
                        ) : React.cloneElement(getIconForType(link.type), {
                            sx: {
                                color: cardAccentColor,
                                fontSize: design2Theme.contactIconInner.fontSize,
                            },
                        })}
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: design2Theme.contactLabel.textTransform,
                                color: design2Theme.contactLabel.color,
                                fontSize: design2Theme.contactLabel.fontSize,
                                fontWeight: design2Theme.contactLabel.fontWeight,
                                display: design2Theme.contactLabel.display
                            }}
                        >
                            {link.type}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: design2Theme.contactValue.fontWeight,
                                fontSize: design2Theme.contactValue.fontSize,
                                wordBreak: design2Theme.contactValue.wordBreak,
                            }}
                        >
                            {(() => {
                                if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                    return formatPhoneNumber(link.details.linkUrl, 'SG');
                                }
                                return link.details.linkTitle || link.details.linkUrl;
                            })()}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderSocialLinks = () => {
        if (!socialLinks || socialLinks.length === 0) return null;

        return (
            <Box sx={{ mt: 4, mb: 3 }}>
                <Typography
                    variant="body2"
                    sx={{
                        textAlign: design2Theme.socialTitle.textAlign,
                        color: design2Theme.socialTitle.color,
                        textTransform: design2Theme.socialTitle.textTransform,
                        fontSize: design2Theme.socialTitle.fontSize,
                        mb: 2
                    }}
                >
                    Connect on Social
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 2,
                        px: 2
                    }}
                >
                    {socialLinks.map((link, index) => (
                        link && link.details?.linkUrl ? (
                            <MuiLink
                                key={index}
                                href={link.details.formattedLink}
                                target="_blank"
                                sx={{
                                    textDecoration: 'none'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: design2Theme.socialIcon.width,
                                        height: design2Theme.socialIcon.height,
                                        borderRadius: design2Theme.socialIcon.borderRadius,
                                        background: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 4px 14px ${lighten(cardAccentColor, 0.7)}`,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                        },
                                    }}
                                >
                                    {link.details?.icon ? (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: design2Theme.socialIcon.borderRadius,
                                                overflow: 'hidden',
                                                backgroundImage: `url(${link.details.icon})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                            }}
                                        />
                                    ) : React.cloneElement(getIconForType(link.type), {
                                        sx: {
                                            color: design2Theme.socialIconInner.color,
                                            fontSize: design2Theme.socialIconInner.fontSize,
                                        },
                                    })}
                                </Box>
                            </MuiLink>
                        ) : null
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: cardThemeValues.container.maxWidth,
                boxSizing: 'border-box',
                padding: cardThemeValues.container.padding,
                margin: 'auto',
                position: 'relative',
                fontFamily: '"Kumbh Sans", sans-serif',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    position: 'relative',
                    boxShadow: design2Theme.mainContainer.boxShadow,
                    borderRadius: design2Theme.mainContainer.borderRadius,
                    overflow: design2Theme.mainContainer.overflow
                }}
            >
                <Box
                    sx={{
                        height: design2Theme.header.height,
                        position: 'relative',
                        zIndex: 1,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0)',
                            zIndex: 1
                        }
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 0,
                            backgroundImage: cardCoverPhotoUrl
                                ? `url(${cardCoverPhotoUrl})`
                                : `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            padding: design2Theme.header.padding,
                            position: 'relative',
                            zIndex: 2
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center'
                        }}>
                            <Button
                                sx={{
                                    minWidth: 'auto',
                                    p: '8px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                    }
                                }}
                            >
                                <QrCodeIcon
                                    fontSize="small"
                                    sx={{
                                        color: '#000000'
                                    }}
                                />
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: cardTheme ? lighten(cardTheme, 0.97) : '#fff',
                        color: cardTheme ? '#333' : '#333',
                        borderRadius: design2Theme.profileCard.borderRadius,
                        padding: design2Theme.profileCard.padding,
                        display: 'flex',
                        boxShadow: design2Theme.profileCard.boxShadow,
                        width: design2Theme.profileCard.width,
                        maxWidth: design2Theme.profileCard.maxWidth,
                        position: 'absolute',
                        top: design2Theme.profileCard.top,
                        left: 0,
                        right: 0,
                        margin: '0 auto',
                        zIndex: design2Theme.profileCard.zIndex,
                        marginBottom: design2Theme.profileCard.marginBottom,
                    }}
                >
                    <Box
                        sx={{
                            width: design2Theme.avatar.width,
                            height: contextCardLayout === 'portrait' ? '100%' : 'auto',
                        }}
                    >
                        <Avatar
                            src={cardProfilePictureUrl || avatarPlaceholder}
                            sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: design2Theme.avatar.minHeight,
                                border: design2Theme.avatar.border,
                                boxShadow: design2Theme.avatar.boxShadow,
                                borderRadius: design2Theme.avatar.borderRadius
                            }}
                            variant={contextCardLayout === 'portrait' ? 'square' : 'rounded'}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: design2Theme.profileInfo.width,
                            padding: design2Theme.profileInfo.padding,
                            position: 'relative',
                        }}
                    >
                        {cardCompanyLogoUrl && (
                            <Avatar
                                src={cardCompanyLogoUrl}
                                sx={{
                                    width: design2Theme.companyLogo.size,
                                    height: design2Theme.companyLogo.size,
                                    border: design2Theme.companyLogo.border,
                                    boxShadow: design2Theme.companyLogo.boxShadow,
                                    position: design2Theme.companyLogo.position,
                                    top: design2Theme.companyLogo.top,
                                    right: design2Theme.companyLogo.right,
                                }}
                            />
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: design2Theme.nameTypography.fontWeight,
                                    fontSize: design2Theme.nameTypography.fontSize,
                                    marginBottom: design2Theme.nameTypography.marginBottom,
                                    wordBreak: design2Theme.nameTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                    marginRight: '0',
                                }}
                            >
                                {cardFullName}
                            </Typography>
                            {/* {cardPronoun && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.8rem',
                                        opacity: 0.8,
                                        fontFamily: '"Kumbh Sans", sans-serif',
                                        fontWeight: 400,
                                        alignSelf: 'center',
                                        marginTop: '2px',
                                    }}
                                >
                                    ({formattedPronoun})
                                </Typography>
                            )} */}
                        </Box>

                        {cardJobTitle && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: design2Theme.jobTitleTypography.fontWeight,
                                    fontSize: design2Theme.jobTitleTypography.fontSize,
                                    marginBottom: design2Theme.jobTitleTypography.marginBottom,
                                    wordBreak: design2Theme.jobTitleTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardJobTitle}
                            </Typography>
                        )}

                        {cardCompany && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design2Theme.companyTypography.fontSize,
                                    marginBottom: design2Theme.companyTypography.marginBottom,
                                    wordBreak: design2Theme.companyTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardCompany}
                            </Typography>
                        )}

                        {cardLocation && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design2Theme.locationTypography.fontSize,
                                    opacity: design2Theme.locationTypography.opacity,
                                    wordBreak: design2Theme.locationTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardLocation}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: design2Theme.aboutContainer.backgroundColor,
                        borderTopLeftRadius: design2Theme.aboutContainer.borderTopLeftRadius,
                        borderTopRightRadius: design2Theme.aboutContainer.borderTopRightRadius,
                        paddingTop: design2Theme.aboutContainer.paddingTop,
                        paddingBottom: design2Theme.aboutContainer.paddingBottom,
                        position: 'relative',
                        zIndex: design2Theme.aboutContainer.zIndex,
                        marginTop: design2Theme.aboutContainer.marginTop,
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: secondaryAccentColor,
                            margin: design2Theme.tabs.margin,
                            width: design2Theme.tabs.width,
                            borderRadius: design2Theme.tabs.borderRadius,
                            padding: design2Theme.tabs.padding,
                            display: 'flex',
                            justifyContent: 'space-around',
                            boxShadow: design2Theme.tabs.boxShadow,
                            position: 'relative',
                            zIndex: design2Theme.tabs.zIndex,
                            fontFamily: '"Kumbh Sans", sans-serif',
                        }}
                    >
                        <Box
                            sx={{
                                padding: design2Theme.tabButton.padding,
                                backgroundColor: '#fff',
                                borderRadius: design2Theme.tabButton.borderRadius,
                                fontWeight: design2Theme.tabButton.fontWeight,
                                fontSize: design2Theme.tabButton.fontSize,
                                flex: design2Theme.tabButton.flex,
                                textAlign: design2Theme.tabButton.textAlign
                            }}
                        >
                            Contact
                        </Box>
                        <Box
                            sx={{
                                padding: design2Theme.tabButton.padding,
                                fontWeight: design2Theme.tabButton.fontWeight,
                                fontSize: design2Theme.tabButton.fontSize,
                                flex: design2Theme.tabButton.flex,
                                textAlign: design2Theme.tabButton.textAlign
                            }}
                        >
                            Social
                        </Box>
                        <Box
                            sx={{
                                padding: design2Theme.tabButton.padding,
                                fontWeight: design2Theme.tabButton.fontWeight,
                                fontSize: design2Theme.tabButton.fontSize,
                                flex: design2Theme.tabButton.flex,
                                textAlign: design2Theme.tabButton.textAlign
                            }}
                        >
                            Services
                        </Box>
                    </Box>

                    {cardBio && (
                        <Box sx={{ padding: design2Theme.bioSection.padding }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design2Theme.bioTypography.fontSize,
                                    lineHeight: design2Theme.bioTypography.lineHeight,
                                    textAlign: design2Theme.bioTypography.textAlign,
                                    color: design2Theme.bioTypography.color,
                                    whiteSpace: design2Theme.bioTypography.whiteSpace,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardBio}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ padding: design2Theme.aboutSection.padding }}>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: design2Theme.sectionTitle.textAlign,
                                color: design2Theme.sectionTitle.color,
                                textTransform: design2Theme.sectionTitle.textTransform,
                                fontSize: design2Theme.sectionTitle.fontSize,
                                mb: design2Theme.sectionTitle.marginBottom,
                                fontFamily: '"Kumbh Sans", sans-serif',
                            }}
                        >
                            About {cardFullName.split(' ')[0]}
                        </Typography>

                        {contactLinks.map((link, index) => renderLinkCard(link, index))}

                        {websiteLinks.map((link, index) => renderLinkCard(link, index))}

                        {renderSocialLinks()}
                    </Box>

                    <Box
                        sx={{
                            padding: design2Theme.saveButtonContainer.padding,
                            display: design2Theme.saveButtonContainer.display,
                            justifyContent: design2Theme.saveButtonContainer.justifyContent
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSaveContact}
                            sx={{
                                width: design2Theme.saveButton.width,
                                borderRadius: design2Theme.saveButton.borderRadius,
                                padding: design2Theme.saveButton.padding,
                                fontWeight: design2Theme.saveButton.fontWeight,
                                textTransform: design2Theme.saveButton.textTransform,
                                fontSize: design2Theme.saveButton.fontSize,
                                background: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                                boxShadow: `0 4px 14px ${lighten(cardAccentColor, 0.7)}`,
                                transition: design2Theme.saveButton.transition,
                                fontFamily: '"Kumbh Sans", sans-serif',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    background: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                                },
                            }}
                        >
                            Save Contact
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CardDesign2;